import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";

import useBasicData from "@/hooks/useBasicData";

const MAX_STYLE_HISTORY_SIZE = 5;

type StyleHistoryEntry = {
  lastTimeUsed: number;
};

type StyleHistoryMap = Record<string, StyleHistoryEntry>;

const trimToMaxSize = (history: StyleHistoryMap): StyleHistoryMap => {
  const entries = Object.entries(history);
  if (entries.length <= MAX_STYLE_HISTORY_SIZE) return history;

  return Object.fromEntries(
    entries
      .sort(([, a], [, b]) => b.lastTimeUsed - a.lastTimeUsed)
      .slice(0, MAX_STYLE_HISTORY_SIZE),
  );
};

export const useStyleHistory = () => {
  const { workspace } = useBasicData();

  if (!workspace) throw new Error("Workspace not found");

  const [styleHistory, setStyleHistory] = useLocalStorage<StyleHistoryMap>(
    `style-history:${workspace.id}`,
    {},
  );

  const setStyleLastTimeUsed = useCallback(
    (styleId: string, timestamp = Date.now()) => {
      setStyleHistory((prev) =>
        trimToMaxSize({ ...prev, [styleId]: { lastTimeUsed: timestamp } }),
      );
    },
    [setStyleHistory],
  );

  const setStylesLastTimeUsed = useCallback(
    (styleIds?: string[], timestamp = Date.now()) => {
      if (!styleIds?.length) return;
      setStyleHistory((prev) =>
        trimToMaxSize({
          ...prev,
          ...Object.fromEntries(
            styleIds.map((styleId) => [styleId, { lastTimeUsed: timestamp }]),
          ),
        }),
      );
    },
    [setStyleHistory],
  );

  const getStyleLastTimeUsed = useCallback(
    (styleId: string) => styleHistory[styleId]?.lastTimeUsed,
    [styleHistory],
  );

  const getStylesLastTimeUsed = useCallback(
    (styleIds: string[]) =>
      styleIds.reduce<Record<string, number>>((acc, id) => {
        const ts = getStyleLastTimeUsed(id);
        if (ts) acc[id] = ts;
        return acc;
      }, {}),
    [getStyleLastTimeUsed],
  );

  const sortStyleIdsByLastTimeUsed = useCallback(
    // Sort by last-time-used (most recent first), unknowns last
    (aId: string, bId: string) => {
      const aTs = getStyleLastTimeUsed(aId) ?? -Infinity;
      const bTs = getStyleLastTimeUsed(bId) ?? -Infinity;
      return bTs - aTs;
    },
    [getStyleLastTimeUsed],
  );

  const getRecentStyleIds = useCallback(() => {
    return Object.keys(styleHistory).sort(sortStyleIdsByLastTimeUsed);
  }, [styleHistory, sortStyleIdsByLastTimeUsed]);

  return {
    setStyleLastTimeUsed,
    setStylesLastTimeUsed,
    getStyleLastTimeUsed,
    getStylesLastTimeUsed,
    sortStyleIdsByLastTimeUsed,
    getRecentStyleIds,
  };
};
